import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ApiError } from '../lib/api.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { Icon } from '../components/Icon';
import { Button } from '../components/ui/Button.js';
import { IconButton } from '../components/ui/IconButton.js';
import { Pressable } from '../components/ui/Pressable.js';
import { useConfirm } from '../components/ui/ConfirmDialog.js';
import { Dropdown } from '../components/ui/Dropdown.js';
import { Recorrido } from '../components/ui/Recorrido.js';
import { useRecorrido } from '../hooks/useRecorrido.js';
import { FilterRow } from '../components/ui/FilterRow.js';
import { SelectionActionBar } from '../components/ui/SelectionActionBar.js';
import { IconPillToggle } from '../components/ui/IconPillToggle.js';
import { Modal } from '../components/ui/Modal.js';
import { SegmentedControl } from '../components/ui/SegmentedControl.js';
import { Input } from '../components/ui/Input.js';
import { Chip } from '../components/ui/Chip.js';
import { useEmailCategoryDefs } from '../hooks/useEmailCategories.js';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { esCuerpoHtml, textoPlanoACuerpoHtml } from '@evo-hub/shared';
import type { TFunction } from 'i18next';
import {
  useInfiniteMessages,
  useSentMessages,
  useEmailCategories,
  useEmailStats,
  useEmailDetail,
  useEmailRuns,
  
  useUpdateCategory,
  useBulkTrash,
  useBulkRestore,
  usePermanentDelete,
  useEmptyTrash,
  useToggleFollowup,
  usePropagateSenderCategory,
  useBulkCategorizeMessages,
  useBulkToggleFollowup,
  useMarkAsRead,
  useMarkAsUnread,
  useComposeDraft,
  useDrafts,
  useDiscardDraft,
  useMessageAttachments,
  useThreads,
  useThreadDetail,
  useBulkThreadAction,
  type EmailMessage,
  type ComposeDraft,
  type ComposeType,
  type DraftRow,
  type ThreadSummary,
  type ThreadDetail,
} from '../hooks/useEmail.js';
import { TimeBandedList } from '../components/common/TimeBandedList.js';
import { ListItem } from '../components/ui/ListItem.js';
import { ListToolbar } from '../components/ui/ListToolbar.js';
import { Checkbox } from '../components/ui/Checkbox.js';
import { PillGroup, Pill } from '../components/PillGroup.js';
import { PropagationModal } from '../components/PropagationModal.js';
import { EmailClaudePanel } from '../components/email/EmailClaudePanel.js';
import { ComposeClaudePanel } from '../components/email/ComposeClaudePanel.js';
import { EmailComposer, draftRowToComposeDraft } from '../components/email/EmailComposer.js';
import { ContactDecisionModal } from '../components/contacts/ContactDecisionModal.js';
import { useListKeyboardNav } from '../hooks/useListKeyboardNav.js';
import { useTopBarCtx } from '../contexts/TopBarContext.js';

// ── Helpers ───────────────────────────────────────────────────────────────────

function AttachmentIcon({ mimeType }: { mimeType: string }) {
  const label = mimeType.startsWith('image/') ? 'img'
    : mimeType === 'application/pdf' ? 'pdf'
    : mimeType.includes('word') || mimeType.includes('document') ? 'doc'
    : mimeType.includes('sheet') || mimeType.includes('excel') ? 'xls'
    : 'file';
  return (
    <span
      className="text-xs font-bold rounded px-1"
      style={{ background: 'var(--evo-cyan)22', color: 'var(--evo-cyan)', fontSize: '9px', lineHeight: '16px' }}
    >
      {label}
    </span>
  );
}

function ago(iso: string, t: TFunction): string {
  const ms = Date.now() - new Date(iso.endsWith('Z') ? iso : iso + 'Z').getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return t('common.timeAgo.justNow');
  if (mins < 60) return t('common.timeAgo.minutes', { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t('common.timeAgo.hours', { count: hrs });
  return t('common.timeAgo.days', { count: Math.floor(hrs / 24) });
}

function fmtDate(iso: string): string {
  return new Date(iso.endsWith('Z') ? iso : iso + 'Z').toLocaleString('es', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function sanitizeHtml(html: string): string {
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/\son\w+=["'][^"']*["']/gi, '')
    .replace(/javascript:/gi, '');
}

/** Lo que va dentro del <body> del visor. Un correo de solo texto se muestra
 *  con sus saltos de línea: puesto tal cual en HTML, salía en un solo párrafo. */
function cuerpoParaVisor(body: string): string {
  return esCuerpoHtml(body) ? sanitizeHtml(body) : textoPlanoACuerpoHtml(body);
}

function stripHtml(html: string): string {
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s{2,}/g, '\n')
    .trim();
}

function EmailBodyFrame({ html }: { html: string }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const safe = cuerpoParaVisor(html);
  const srcdoc = `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank"><style>
html,body{background:#ffffff!important;color:#111111!important;margin:0;padding:12px;font-family:system-ui,sans-serif;font-size:13px;line-height:1.6}
a{color:#0066cc!important}img{max-width:100%!important}
</style></head><body>${safe}</body></html>`;

  function handleLoad() {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    iframeRef.current.style.height = `${doc.documentElement.scrollHeight + 24}px`;
  }

  return (
    <iframe
      ref={iframeRef}
      srcDoc={srcdoc}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      title="email-body"
      style={{ width: '100%', border: 'none', minHeight: '120px', borderRadius: 'var(--evo-r-lg)', background: '#ffffff' }}
      onLoad={handleLoad}
    />
  );
}

// ── Detail panel ──────────────────────────────────────────────────────────────
// Colores de categoría: antes CATEGORY_COLORS hardcodeado acá (taxonomía
// personal de una sola cuenta). Ahora viene de /api/email-categories (por
// cuenta, editable en Settings > Categorías) — ver useEmailCategories.

type BodyMode = 'email' | 'text' | 'thread';

function DetailPanel({ messageId, onClose, categoryOptions, onCategoryChange, onSelectMessage, onCompose }: {
  messageId: number; onClose: () => void;
  categoryOptions: string[]; onCategoryChange: (id: number, cat: string, fromEmail?: string | null) => void;
  onSelectMessage?: (id: number) => void;
  onCompose: (draft: ComposeDraft, type: ComposeType) => void;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<BodyMode>('email');
  const [followupModal, setFollowupModal] = useState<boolean | null>(null);
  const [categoryModal, setCategoryModal] = useState<{ category: string } | null>(null);
  const [decisionModal, setDecisionModal] = useState(false);
  const [decisionToast, setDecisionToast] = useState('');
  const { data, isLoading, isError, error, refetch } = useEmailDetail(messageId);
  const { data: emailCategories = [] } = useEmailCategoryDefs();
  const { data: attachments = [] } = useMessageAttachments(messageId);
  const toggleFollowup = useToggleFollowup();
  const propagate = usePropagateSenderCategory();
  const composeDraftMutation = useComposeDraft();
  const detail = data?.detail;
  const thread = data?.thread ?? [];

  useEffect(() => {
    if (thread.length <= 1 && mode === 'thread') setMode('email');
  }, [thread.length, mode]);

  useEffect(() => {
    if (!detail) return;
    if (detail.direction === 'inbound' && detail.contactId === null && detail.contactDecision === null) {
      setDecisionModal(true);
    } else {
      setDecisionModal(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: granular deps on specific fields; avoids re-run when unrelated detail fields change
  }, [detail?.id, detail?.direction, detail?.contactId, detail?.contactDecision]);

  function handleCompose(type: ComposeType) {
    if (!detail) return;
    composeDraftMutation.mutate(
      { id: detail.id, type },
      { onSuccess: (draft) => onCompose(draft, type) }
    );
  }

  function handleFollowupToggle() {
    if (!detail) return;
    setFollowupModal(detail.needsFollowup === 0);
  }

  function doToggle(learn: boolean, learnScope?: 'sender' | 'domain') {
    if (!detail || followupModal === null) return;
    toggleFollowup.mutate({
      id: detail.id,
      value: followupModal,
      fromEmail: detail.fromEmail,
      ...(learn && learnScope ? { learn: true, learnScope } : {}),
    });
    setFollowupModal(null);
  }

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <LoadingState size="sm" />
      </div>
    );
  }
  if (isError) {
    // Bug real (2026-08-05): antes, cualquier fallo de fetch (un 500
    // transitorio, no solo un 404 real) mostraba el mismo "not found" — el
    // usuario veía "no encontrado" para un email que sí existía. Un 404 real
    // del servidor sí significa "no existe"; cualquier otro error (red, 500)
    // es un fallo de carga con botón de reintentar, nunca "no encontrado".
    const isRealNotFound = error instanceof ApiError && error.status === 404;
    if (isRealNotFound) {
      return <div className="flex-1 flex items-center justify-center text-xs" style={{ color: 'var(--evo-fg-3)' }}>{t('email.notFound')}</div>;
    }
    return (
      <div className="flex-1 flex items-center justify-center">
        <EmptyState
          title={t('email.loadFailed.title', 'No se pudo cargar el email')}
          description={t('email.loadFailed.description', 'Puede ser un problema de conexión.')}
          action={<Button variant="outline" tone="muted" size="sm" onClick={() => { void refetch(); }}>{t('email.loadFailed.retryBtn', 'Reintentar')}</Button>}
        />
      </div>
    );
  }
  if (!detail) {
    return <div className="flex-1 flex items-center justify-center text-xs" style={{ color: 'var(--evo-fg-3)' }}>{t('email.notFound')}</div>;
  }

  const catColor = emailCategories.find(c => c.name === detail.classifiedAs)?.color ?? 'var(--evo-fg-3)';

  return (
    <>
    <div className="flex flex-col h-full">
      {/* Row 1 — action header (fixed height, DS pattern) */}
      <div
        className="shrink-0"
        style={{
          height: 'var(--evo-toolbar-height)',
          padding: '0 var(--evo-content-pad-x) 0 var(--evo-space-5)',
          borderBottom: '1px solid var(--evo-stroke)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--evo-space-2)',
        }}
      >
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2, overflow: 'hidden' }}>
          <span style={{ fontSize: 'var(--evo-fs-body)', fontWeight: 'var(--evo-fw-medium)', color: 'var(--evo-fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {detail.subject || t('email.noSubject')}
          </span>
          <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {detail.fromEmail}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 'var(--evo-space-2)', flexShrink: 0, alignItems: 'center' }}>
          {/* V2: acciones frecuentes y reconocibles → botón de solo icono con
              tooltip. reply y reply-all llevan glifos DISTINTOS (flecha simple
              vs doble) — nunca dos acciones con el mismo dibujo. */}
          {([
            { icon: 'reply' as const, type: 'reply' as ComposeType, label: t('email.reply') },
            { icon: 'reply-all' as const, type: 'reply-all' as ComposeType, label: t('email.replyAll') },
            { icon: 'forward' as const, type: 'forward' as ComposeType, label: t('email.forward') },
          ]).map(({ icon, type, label }) => (
            <IconButton key={type}
              variant="outline"
              tone="muted"
              size="md"
              disabled={composeDraftMutation.isPending}
              onClick={() => handleCompose(type)}
              icon={<Icon name={icon} size={15} />}
              ariaLabel={label}
              tooltip={label}
            />
          ))}
          <IconButton variant="ghost" tone="muted" size="sm" icon={<Icon name="close" size={14} />} ariaLabel={t('common.close')} onClick={onClose} />
        </div>
      </div>

      {/* Row 2 — metadata */}
      <div
        className="shrink-0 flex items-center gap-2 flex-wrap"
        style={{
          padding: '0 var(--evo-content-pad-x) 0 var(--evo-space-5)',
          minHeight: 'var(--evo-toolbar-height)',
          borderBottom: '1px solid var(--evo-stroke)',
        }}
      >
        <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-3)' }}>{fmtDate(detail.timestamp)}</span>
        <span style={{ color: 'var(--evo-stroke)' }}>·</span>
        <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-3)' }}>{detail.account || t('email.unknownAccount')}</span>
        <span style={{ color: 'var(--evo-stroke)' }}>·</span>
        <Dropdown<string>
          value={detail.classifiedAs ?? ''}
          options={[
            { value: '', label: t('email.noCategory') },
            ...categoryOptions.map(c => ({ value: c, label: t(`categories.${c}`, { defaultValue: c }) })),
          ]}
          onChange={newCat => {
            if (newCat && detail.fromEmail) {
              setCategoryModal({ category: newCat });
            } else {
              onCategoryChange(detail.id, newCat);
            }
          }}
          size="xs"
          triggerStyle={{ height: 'var(--evo-pill-h-sm)', background: `${catColor}22`, color: catColor, border: `1px solid ${catColor}44`, borderRadius: 'var(--evo-r-pill)' }}
        />
        {/* Toggle de seguimiento (spec V2): bell amber con relleno cuando
            está marcado, bell-off gris cuando no. */}
        <IconPillToggle
          icon="bell-off"
          activeIcon="bell"
          active={!!detail.needsFollowup}
          label={detail.needsFollowup ? t('email.followupToggle.modalTitleRemove') : t('email.followupToggle.modalTitleMark')}
          onClick={handleFollowupToggle}
          disabled={toggleFollowup.isPending}
          tooltipAlign="left"
        />
        <div style={{ marginLeft: 'auto' }}>
          <SegmentedControl<BodyMode>
            value={mode}
            onChange={setMode}
            size="sm"
            options={((thread.length > 1 ? ['email', 'text', 'thread'] : ['email', 'text']) as BodyMode[]).map((m) => ({
              value: m,
              label: m === 'email' ? t('email.viewEmail') : m === 'text' ? t('email.viewText') : t('email.threadCount', { count: thread.length }),
            }))}
          />
        </div>
      </div>

      <PropagationModal
        open={followupModal !== null}
        onClose={() => setFollowupModal(null)}
        fromEmail={detail?.fromEmail ?? ''}
        actionType="followup"
        newValue={followupModal ?? false}
        onThisOnly={() => doToggle(false)}
        onSender={() => doToggle(true, 'sender')}
        onDomain={() => doToggle(true, 'domain')}
        isPending={toggleFollowup.isPending}
      />
      <PropagationModal
        open={categoryModal !== null && detail !== undefined}
        onClose={() => setCategoryModal(null)}
        fromEmail={detail?.fromEmail ?? ''}
        actionType="category"
        newValue={categoryModal?.category ?? ''}
        onThisOnly={() => { if (detail && categoryModal) { onCategoryChange(detail.id, categoryModal.category, detail.fromEmail); } setCategoryModal(null); }}
        onSender={() => { if (detail && categoryModal) { propagate.mutate({ id: detail.id, fromEmail: detail.fromEmail!, category: categoryModal.category, scope: 'sender' }); } setCategoryModal(null); }}
        onDomain={() => { if (detail && categoryModal) { propagate.mutate({ id: detail.id, fromEmail: detail.fromEmail!, category: categoryModal.category, scope: 'domain' }); } setCategoryModal(null); }}
        isPending={propagate.isPending}
      />

      {decisionModal && detail?.fromEmail && (
        <ContactDecisionModal
          messageId={detail.id}
          fromEmail={detail.fromEmail}
          onClose={() => setDecisionModal(false)}
          onDecided={(decision) => {
            setDecisionModal(false);
            const msg = decision === 'dismissed' ? t('contact.decision.toast_dismissed')
              : decision === 'saved_with_markdown' ? t('contact.decision.toast_saved_with_markdown')
              : t('contact.decision.toast_saved_only');
            setDecisionToast(msg);
            setTimeout(() => setDecisionToast(''), 3000);
          }}
        />
      )}
      {decisionToast && (
        <div
          className="fixed bottom-6 right-6 z-50 px-4 py-2 rounded-lg text-sm font-medium shadow-lg"
          style={{ background: 'var(--evo-cyan)', color: '#fff', pointerEvents: 'none' }}
        >
          {decisionToast}
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto" style={{ padding: 'var(--evo-pane-pad)', boxSizing: 'border-box' }}>
        {mode === 'email' && (
          detail.body
            ? <EmailBodyFrame html={detail.body} />
            : <p style={{ color: 'var(--evo-fg-3)', fontSize: '13px', fontStyle: 'italic' }}>
                {t('email.bodyUnavailable')}
              </p>
        )}
        {mode === 'text' && (
          detail.body
            ? <pre style={{ fontSize: '12px', color: 'var(--evo-fg-2)', whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                {esCuerpoHtml(detail.body) ? stripHtml(detail.body) : detail.body}
              </pre>
            : <p style={{ color: 'var(--evo-fg-3)', fontSize: '13px', fontStyle: 'italic' }}>{t('email.noText')}</p>
        )}
        {mode === 'thread' && (
          thread.length <= 1
            ? <p style={{ color: 'var(--evo-fg-3)', fontSize: '13px', fontStyle: 'italic' }}>{t('email.noThread')}</p>
            : <div className="space-y-2">
                {thread.map(item => {
                  const isCurrent = item.id === messageId;
                  return (
                    <div
                      key={item.id}
                      onClick={() => !isCurrent && onSelectMessage?.(item.id)}
                      className="rounded p-3"
                      style={{
                        background: isCurrent ? 'var(--evo-cyan)22' : 'var(--evo-bg-2)',
                        border: `1px solid ${isCurrent ? 'var(--evo-cyan)' : 'var(--evo-stroke)'}`,
                        cursor: isCurrent ? 'default' : 'pointer',
                      }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium truncate" style={{ color: 'var(--evo-fg-2)' }}>
                          {item.fromEmail ?? '—'}
                        </span>
                        <span className="text-xs shrink-0" style={{ color: 'var(--evo-fg-3)' }}>
                          {fmtDate(item.timestamp)}
                        </span>
                      </div>
                      <div className="text-xs mt-0.5 truncate" style={{ color: 'var(--evo-fg-3)' }}>
                        {item.subject || t('email.noSubject')}
                      </div>
                    </div>
                  );
                })}
              </div>
        )}
      </div>
    </div>
      {/* Attachments */}
      {attachments.length > 0 && (
        <div
          className="shrink-0 px-4 py-2"
          style={{ borderTop: '1px solid var(--evo-stroke)' }}
        >
          <div className="text-xs font-semibold mb-1.5" style={{ color: 'var(--evo-fg-3)' }}>
            {t('email.attachments')}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {attachments.map(att => (
              <a
                key={att.id}
                href={`/api/email/attachments/${att.id}/download`}
                download={att.filename}
                className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded"
                style={{
                  background: 'var(--evo-bg-2)',
                  border: '1px solid var(--evo-stroke)',
                  color: 'var(--evo-fg-2)',
                  textDecoration: 'none',
                }}
              >
                <AttachmentIcon mimeType={att.mimeType} />
                <span>{att.filename}</span>
                <span style={{ color: 'var(--evo-fg-3)' }}>
                  ({att.sizeBytes < 1024 * 1024
                    ? `${(att.sizeBytes / 1024).toFixed(0)}KB`
                    : `${(att.sizeBytes / (1024 * 1024)).toFixed(1)}MB`})
                </span>
              </a>
            ))}
          </div>
        </div>
      )}

    </>
  );
}

// ── Claude panel ──────────────────────────────────────────────────────────────

type ClaudeContext =
  | { type: 'email'; messageId: number }
  | { type: 'compose'; recipient: string; draft?: { subject?: string; body?: string } }
  | { type: 'idle' };

function ClaudePanel({ context }: { context: ClaudeContext }) {
  if (context.type === 'compose') {
    return <ComposeClaudePanel recipient={context.recipient} draft={context.draft} />;
  }
  const emailMessageId = context.type === 'email' ? context.messageId : null;
  return <EmailClaudePanel messageId={emailMessageId} />;
}

// ── Runs table ────────────────────────────────────────────────────────────────

function RunsTable() {
  const { t } = useTranslation();
  const { data: runs = [], isLoading } = useEmailRuns(30);
  function dur(r: { startedAt: string; finishedAt: string | null }) {
    if (!r.finishedAt) return '—';
    return `${Math.round((new Date(r.finishedAt + 'Z').getTime() - new Date(r.startedAt + 'Z').getTime()) / 1000)}s`;
  }
  const headers = [
    t('email.runs.colId'),
    t('email.runs.colType'),
    t('email.runs.colTrigger'),
    t('email.runs.colStatus'),
    t('email.runs.colProcessed'),
    t('email.runs.colDuration'),
    t('email.runs.colStart'),
  ];
  return (
    <div className="overflow-auto rounded" style={{ border: '1px solid var(--evo-stroke)' }}>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr style={{ background: 'var(--evo-bg-2)', borderBottom: '1px solid var(--evo-stroke)' }}>
            {headers.map(h => (
              <th key={h} className="px-3 py-2 text-left font-medium" style={{ color: 'var(--evo-fg-3)' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading && <tr><td colSpan={7} className="px-3 py-6 text-center" style={{ color: 'var(--evo-fg-3)' }}>{t('email.loading')}</td></tr>}
          {!isLoading && runs.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center" style={{ color: 'var(--evo-fg-3)' }}>{t('email.runs.noHistory')}</td></tr>}
          {runs.map(r => (
            <tr key={r.id} style={{ borderBottom: '1px solid var(--evo-stroke)' }}>
              <td className="px-3 py-2 font-mono">{r.id}</td>
              <td className="px-3 py-2">{r.type}</td>
              <td className="px-3 py-2" style={{ color: 'var(--evo-fg-3)' }}>{r.trigger}</td>
              <td className="px-3 py-2">
                <span style={{ color: r.status === 'success' ? '#00fac8' : r.status === 'error' ? '#ef4444' : '#f59e0b' }}>{r.status}</span>
              </td>
              <td className="px-3 py-2 text-right" style={{ color: 'var(--evo-fg-3)' }}>{r.itemsProcessed}</td>
              <td className="px-3 py-2" style={{ color: 'var(--evo-fg-3)' }}>{dur(r)}</td>
              <td className="px-3 py-2" style={{ color: 'var(--evo-fg-3)' }}>{fmtDate(r.startedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── TopBar actions for email page ────────────────────────────────────────────

function EmailTopBarActions({ onCompose }: { onCompose: () => void }) {
  const { t } = useTranslation();
  return (
    <PillGroup size="md">
      <Pill tone="cyan" icon="compose" onClick={onCompose}>
        {t('email.compose.button')}
      </Pill>
    </PillGroup>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

function trashCountdown(autoDeleteAt: string | null, t: TFunction): string {
  if (!autoDeleteAt) return t('email.trash.never');
  const days = Math.ceil((new Date(autoDeleteAt.endsWith('Z') ? autoDeleteAt : autoDeleteAt + 'Z').getTime() - Date.now()) / 86400_000);
  if (days <= 0) return t('email.trash.today');
  return days === 1 ? t('email.trash.inDays_one', { count: days }) : t('email.trash.inDays_other', { count: days });
}

function ThreadMessageBody({ body }: { body: string }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const safe = cuerpoParaVisor(body);
  const srcDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank"><style>html,body{background:#fff!important;color:#111!important;margin:0;padding:8px;font-family:system-ui,sans-serif;font-size:13px;line-height:1.6}a{color:#0066cc!important}img{max-width:100%!important}</style></head><body>${safe}</body></html>`;
  function handleLoad() {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    iframeRef.current.style.height = `${doc.documentElement.scrollHeight + 16}px`;
  }
  return (
    <iframe
      ref={iframeRef}
      srcDoc={srcDoc}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      title="thread-msg-body"
      style={{ width: '100%', border: 'none', minHeight: '100px', borderRadius: 4 }}
      onLoad={handleLoad}
    />
  );
}

function ThreadDetailView({ detail, onCompose }: {
  detail: ThreadDetail | undefined;
  onCompose: (draft: ComposeDraft, type: ComposeType) => void;
}) {
  const { t } = useTranslation();
  const [expandedIds, setExpandedIds] = React.useState<Set<number>>(new Set());

  React.useEffect(() => {
    if (detail?.messages?.length) {
      const lastId = detail.messages[detail.messages.length - 1]?.id;
      if (lastId !== undefined) setExpandedIds(new Set([lastId]));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: messages are derived from thread; re-run only when thread changes, not on message updates
  }, [detail?.thread?.id]);

  if (!detail) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <LoadingState size="sm" />
      </div>
    );
  }

  const msgs = detail.messages;
  const last = msgs[msgs.length - 1];

  function toggle(id: number) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function getMeta(msg: EmailMessage): { fromEmail: string | null; rfcMessageId: string | null } {
    try {
      const m = JSON.parse((msg as unknown as { metadataJson?: string }).metadataJson ?? '{}') as Record<string, unknown>;
      return {
        fromEmail: typeof m['fromEmail'] === 'string' ? m['fromEmail'] : null,
        rfcMessageId: typeof m['rfcMessageId'] === 'string' ? m['rfcMessageId'] : null,
      };
    } catch { return { fromEmail: null, rfcMessageId: null }; }
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 shrink-0" style={{ height: '56px', borderBottom: '1px solid var(--evo-stroke)' }}>
        <span className="text-xs font-semibold truncate flex-1" style={{ color: 'var(--evo-fg-1)' }}>
          {detail.thread.subjectDisplay || detail.thread.subjectNormalized || t('email.drafts.noSubject')}
        </span>
        <span className="text-xs shrink-0" style={{ color: 'var(--evo-fg-3)' }}>
          {t('email.threads.msgCount', { count: detail.thread.messageCount })}
        </span>
        <Button variant="outline" tone="muted" size="sm" onClick={() => setExpandedIds(new Set(msgs.map(m => m.id)))}>
          {t('email.expandAll')}
        </Button>
        <Button variant="outline" tone="muted" size="sm" onClick={() => setExpandedIds(new Set(last?.id != null ? [last.id] : []))}>
          {t('email.collapseAll')}
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3" style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0 }}>
        {msgs.map((msg, idx) => {
          const meta = getMeta(msg);
          const isExpanded = expandedIds.has(msg.id);
          const isLast = idx === msgs.length - 1;
          return (
            <div key={msg.id} style={{ border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r-md)', background: 'var(--evo-bg-2)', overflow: 'hidden', flexShrink: 0 }}>
              <Pressable fullWidth onPress={() => toggle(msg.id)}>
                <div className="flex items-center gap-2 px-3 py-2 text-left">
                  <span className="text-xs font-medium truncate flex-1" style={{ color: 'var(--evo-fg-2)' }}>
                    {meta.fromEmail ?? msg.fromEmail ?? (msg.direction === 'outbound' ? t('common.you', { defaultValue: 'You' }) : '—')}
                  </span>
                  <span className="text-xs shrink-0" style={{ color: 'var(--evo-fg-3)' }}>{fmtDate(msg.timestamp)}</span>
                  <span style={{ display: 'inline-flex', transform: isExpanded ? 'none' : 'rotate(-90deg)', transition: 'transform 0.15s', flexShrink: 0 }}>
                    <Icon name="chevron-down" size={12} strokeWidth={2} />
                  </span>
                </div>
              </Pressable>
              {!isExpanded && (
                <div className="px-3 pb-2 text-xs truncate" style={{ color: 'var(--evo-fg-3)' }}>
                  {stripHtml(msg.body ?? '').slice(0, 80)}
                </div>
              )}
              {isExpanded && (
                <div className="px-3 pb-3">
                  {msg.body ? (
                    <ThreadMessageBody body={msg.body} />
                  ) : (
                    <div className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>{t('email.noBody', { defaultValue: '(no content)' })}</div>
                  )}
                </div>
              )}
              {isLast && isExpanded && (
                <div className="flex items-center gap-2 px-3 pb-3">
                  {(['reply', 'reply-all', 'forward'] as const).map(key => (
                    <IconButton key={key}
                      variant="outline"
                      tone="muted"
                      size="md"
                      icon={<Icon name={key} size={15} />}
                      ariaLabel={key === 'reply' ? t('email.reply') : key === 'reply-all' ? t('email.replyAll') : t('email.forward')}
                      tooltip={key === 'reply' ? t('email.reply') : key === 'reply-all' ? t('email.replyAll') : t('email.forward')}
                      onClick={() => {
                        const subject = detail.thread.subjectNormalized;
                        const draft: ComposeDraft = {
                          to: meta.fromEmail ? [meta.fromEmail] : [],
                          cc: [],
                          bcc: [],
                          subject: key === 'forward' ? `Fwd: ${subject}` : `Re: ${subject}`,
                          bodyHtml: '',
                          accountName: detail.thread.accountName,
                          inReplyTo: meta.rfcMessageId,
                          references: [],
                          replyToMessageId: msg.id,
                        };
                        const type: ComposeType = key === 'reply' ? 'reply' : key === 'reply-all' ? 'reply-all' : 'forward';
                        onCompose(draft, type);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

type SectionMode = 'main' | 'followup' | 'categories' | 'history';
type ViewFilter = 'all' | 'unread' | 'sent' | 'drafts' | 'all_categories' | 'trash';

export function EmailPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [sectionMode, setSectionMode] = useState<SectionMode>(() => {
    const section = searchParams.get('section') as SectionMode | null;
    if (section === 'followup' || section === 'categories' || section === 'history') return section;
    const tab = searchParams.get('tab');
    if (tab === 'followup') return 'followup';
    if (tab === 'history') return 'history';
    return 'main';
  });
  const [viewFilter, setViewFilter] = useState<ViewFilter>(() => {
    const view = searchParams.get('view') as ViewFilter | null;
    if (view === 'unread' || view === 'sent' || view === 'drafts' || view === 'all_categories' || view === 'trash') return view;
    const tab = searchParams.get('tab');
    if (tab === 'unread') return 'unread';
    if (tab === 'sent') return 'sent';
    if (tab === 'drafts') return 'drafts';
    if (tab === 'uncategorized') return 'all_categories';
    if (tab === 'trash') return 'trash';
    return 'unread';
  });
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(() => {
    const id = searchParams.get('id');
    return id ? parseInt(id) : null;
  });
  const [page, setPage] = useState(1);
  const [emailSearch, setEmailSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set());
  const [checkedDraftIds, setCheckedDraftIds] = useState<Set<number>>(new Set());
  const [newCatModal, setNewCatModal] = useState(false);
  const [newCatInput, setNewCatInput] = useState('');
  const [collapsedBands, setCollapsedBands] = useState<Set<string>>(
    new Set(['this_week', 'this_month', 'older_2_3_months', 'older'])
  );
  const [followupSubFilter, setFollowupSubFilter] = useState<'alta' | 'media' | 'baja' | 'uncertain' | null>(null);
  const [composeState, setComposeState] = useState<{ draft: ComposeDraft; type: ComposeType; draftId?: number } | null>(null);
  const [composerRecipient, setComposerRecipient] = useState('');
  const [viewMode, setViewMode] = useState<'threads' | 'messages'>('messages');
  const [selectedThreadId, setSelectedThreadId] = useState<number | null>(null);
  const [checkedThreadIds, setCheckedThreadIds] = useState<Set<number>>(new Set());
  const { setOverride } = useTopBarCtx();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(emailSearch), 200);
    return () => clearTimeout(timer);
  }, [emailSearch]);

  // Sync URL ?id= param to selectedId so global search navigation works
  useEffect(() => {
    const id = searchParams.get('id');
    if (id) {
      const parsed = parseInt(id);
      if (!isNaN(parsed)) setSelectedId(parsed);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('id')]);

  const { data: stats } = useEmailStats();
  const { data: categories = [] } = useEmailCategories();
  // Las categorías DEFINIDAS de la cuenta (tabla email_categories), distintas
  // de `categories`, que son las que ya tienen mensajes clasificados.
  const { data: categoryDefs = [] } = useEmailCategoryDefs();
  const updateCategory = useUpdateCategory();
  const bulkTrash = useBulkTrash();
  const bulkRestore = useBulkRestore();
  const permanentDelete = usePermanentDelete();
  const emptyTrash = useEmptyTrash();
  const bulkCategorize = useBulkCategorizeMessages();
  const bulkFollowup = useBulkToggleFollowup();
  const markAsRead = useMarkAsRead();
  const markAsUnread = useMarkAsUnread();
  const bulkThreadAction = useBulkThreadAction();

  const { data: drafts = [] } = useDrafts();
  const discardDraftMut = useDiscardDraft();
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();

  async function handleDiscardDraft(id: number) {
    const ok = await confirmAction({ title: t('email.drafts.discardConfirm'), tone: 'danger' });
    if (!ok) return;
    discardDraftMut.mutate(id, {
      // Bug real: el DELETE y el refetch de la lista siempre andaban bien —
      // lo que no se actualizaba era el panel del editor a la derecha si
      // estabas editando JUSTO el draft que acabás de descartar (openDraft
      // deja composeState.draftId apuntando a esa fila; nada lo limpiaba).
      // El endpoint respondía 200 y el draft desaparecía de la lista, pero
      // el editor seguía mostrando el mismo texto — parecía que no había
      // pasado nada.
      onSuccess: () => {
        if (composeState?.draftId === id) {
          setComposeState(null);
          setComposerRecipient('');
        }
      },
    });
  }

  function openDraft(row: DraftRow) {
    const draft = draftRowToComposeDraft(row);
    setComposerRecipient(draft.to[0] ?? '');
    setComposeState({ draft, type: draft.replyToMessageId ? 'reply' : 'compose', draftId: row.id });
  }

  const isTrashView = sectionMode === 'main' && viewFilter === 'trash';
  const isSentView = sectionMode === 'main' && viewFilter === 'sent';
  const isDraftsView = sectionMode === 'main' && viewFilter === 'drafts';
  const isUnclassifiedCategory = activeCategory === '__unclassified__';

  function handleViewChange(newView: ViewFilter) {
    setViewFilter(newView);
    setSectionMode('main');
    setActiveCategory(null);
    setSelectedId(null);
    setPage(1);
    setCheckedIds(new Set());
    setCheckedDraftIds(new Set());
    const params: Record<string, string> = {};
    if (newView !== 'all') params['view'] = newView;
    setSearchParams(params, { replace: true });
  }

  function handleSectionChange(newSection: 'followup' | 'categories') {
    if (sectionMode === newSection) {
      setSectionMode('main');
      setActiveCategory(null);
      setSelectedId(null);
      setPage(1);
      setCheckedIds(new Set());
    setCheckedDraftIds(new Set());
      setSearchParams({}, { replace: true });
    } else {
      setSectionMode(newSection);
      setSelectedId(null);
      setPage(1);
      setCheckedIds(new Set());
    setCheckedDraftIds(new Set());
      setSearchParams({ section: newSection }, { replace: true });
    }
  }


  const isSpamPhishingFilter = sectionMode === 'categories' &&
    (activeCategory === 'Spam Sugerido' || activeCategory === 'Phishing');

  const inboxFilters = {
    ...(sectionMode === 'followup' && followupSubFilter === 'uncertain'
      ? { followupDecision: 'followup_uncertain' }
      : sectionMode === 'followup' && followupSubFilter
      ? { needsFollowup: true, followupPriority: followupSubFilter }
      : sectionMode === 'followup'
      ? { needsFollowup: true }
      : {}),
    ...(sectionMode === 'categories' && activeCategory && !isUnclassifiedCategory
      ? { category: activeCategory }
      : {}),
    ...(sectionMode === 'categories' && isUnclassifiedCategory ? { unclassified: true } : {}),
    ...(sectionMode === 'main' && viewFilter === 'unread' ? { unread: true } : {}),
    ...(sectionMode === 'main' && viewFilter === 'all_categories' ? { unclassified: true } : {}),
    ...(isTrashView ? { trash: true } : {}),
    ...(!isSpamPhishingFilter ? { hideSpam: true } : {}),
    ...(debouncedSearch.length >= 2 ? { search: debouncedSearch } : {}),
    limit: 50,
  };
  const {
    data: inboxInfData,
    isLoading: inboxLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteMessages(inboxFilters);
  const inboxMessages = useMemo(
    () => inboxInfData?.pages.flatMap(p => p.rows) ?? [],
    [inboxInfData],
  );
  const sentinelRef = useRef<HTMLDivElement>(null);
  const { data: sentData, isLoading: sentLoading } = useSentMessages({ page, limit: 50 });
  const sentMessages: EmailMessage[] = sentData?.rows ?? [];
  const { data: threadsData, isLoading: threadsLoading } = useThreads({
    ...(debouncedSearch.length >= 2 ? { search: debouncedSearch } : {}),
    ...(!isSpamPhishingFilter ? { hideSpam: true } : {}),
    page,
    limit: 50,
  });
  const { data: threadDetail } = useThreadDetail(viewMode === 'threads' ? selectedThreadId : null);
  const isLoading = isSentView ? sentLoading : inboxLoading;

  // El desplegable de categorías se armaba SOLO con las que ya tienen correo
  // clasificado. Una cuenta nueva tiene sus 5 categorías por defecto sembradas
  // al aprovisionarla, pero cero mensajes clasificados — así que el filtro
  // salía vacío y parecía que la cuenta no tenía categorías (villa-sion,
  // 2026-08-19). Se unen las DEFINIDAS con las que tienen mensajes: las
  // definidas aparecen desde el día uno, en 0, y una categoría que quedó en
  // mensajes viejos pero ya no está definida no desaparece del filtro.
  const categoriesForFilter = useMemo(() => {
    const conteo = new Map(categories.map(c => [c.category, c.count]));
    const definidas = categoryDefs.map(d => ({ category: d.name, count: conteo.get(d.name) ?? 0 }));
    const soloEnMensajes = categories.filter(c => !categoryDefs.some(d => d.name === c.category));
    return [...definidas, ...soloEnMensajes];
  }, [categories, categoryDefs]);

  const categorizedCount = categories.reduce((sum, c) => sum + c.count, 0);
  const uncategorizedCount = Math.max(0, (stats?.totalMessages ?? 0) - categorizedCount);
  const categoryNames = categories.map(c => c.category);
  const messages: EmailMessage[] = isSentView ? sentMessages : inboxMessages;
  const threads = threadsData?.threads ?? [];

  useListKeyboardNav(messages, selectedId, id => setSelectedId(id as number));
  useListKeyboardNav(threads, selectedThreadId, id => setSelectedThreadId(id as number));

  // Auto-load next page when the sentinel scrolls into view.
  // Guard: skip if all bands are collapsed — collapsed groups have maxHeight:0, which
  // pushes the sentinel up into the viewport even though no content is visible.
  // rootMargin removed (was 200px) so the sentinel must be genuinely visible to trigger.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const ALL_BANDS = ['today', 'yesterday', 'this_week', 'this_month', 'older_2_3_months', 'older'];
    const observer = new IntersectionObserver(entries => {
      const hasOpenBand = ALL_BANDS.some(b => !collapsedBands.has(b));
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage && hasOpenBand) {
        void fetchNextPage();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, collapsedBands]);

  useEffect(() => {
    const onCompose = () => {
      setComposerRecipient('');
      setComposeState({ draft: { accountName: '', to: [], cc: [], bcc: [], subject: '', bodyHtml: '', inReplyTo: null, references: [], replyToMessageId: null }, type: 'compose' });
    };
    setOverride({
      title: t('email.pageTitle'),
      heroIcon: 'email',
      bottomActions: <EmailTopBarActions onCompose={onCompose} />,
    });
    return () => setOverride({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggleBand(band: string) {
    setCollapsedBands(prev => {
      const next = new Set(prev);
      next.has(band) ? next.delete(band) : next.add(band);
      return next;
    });
  }

  function collapseAll() {
    setCollapsedBands(new Set(['today', 'yesterday', 'this_week', 'this_month', 'older_2_3_months', 'older']));
  }

  function expandAll() {
    setCollapsedBands(new Set());
  }

  function handleBandCheck(ids: number[], allChecked: boolean) {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (allChecked) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  }

  function toggleCheck(id: number, e?: React.MouseEvent) {
    e?.stopPropagation();
    setCheckedIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  }

  function toggleCheckDraft(id: number, e?: React.MouseEvent) {
    e?.stopPropagation();
    setCheckedDraftIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  }

  function handleBulkDiscardDrafts() {
    if (composeState?.draftId !== undefined && checkedDraftIds.has(composeState.draftId)) {
      setComposeState(null);
      setComposerRecipient('');
    }
    checkedDraftIds.forEach(id => discardDraftMut.mutate(id));
    setCheckedDraftIds(new Set());
  }

  function toggleCheckAll() {
    if (checkedIds.size === messages.length && messages.length > 0) setCheckedIds(new Set());
    else setCheckedIds(new Set(messages.map(m => m.id)));
  }

  function toggleCheckThread(id: number) {
    setCheckedThreadIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  }

  function toggleCheckAllThreads() {
    const threads = threadsData?.threads ?? [];
    if (checkedThreadIds.size === threads.length && threads.length > 0) setCheckedThreadIds(new Set());
    else setCheckedThreadIds(new Set(threads.map(t => t.id)));
  }

  function handleSelectEmail(id: number, isRead: number) {
    setSelectedId(id);
    if (!isRead) markAsRead.mutate(id);
    const params: Record<string, string> = { id: String(id) };
    if (sectionMode !== 'main') params['section'] = sectionMode;
    else if (viewFilter !== 'all') params['view'] = viewFilter;
    setSearchParams(params, { replace: true });
  }

  function handleCategoryChange(cat: string | null) {
    setActiveCategory(cat);
    setSectionMode(cat ? 'categories' : 'main');
    setSelectedId(null); setPage(1); setCheckedIds(new Set());
    const params: Record<string, string> = {};
    if (cat) params['category'] = cat;
    setSearchParams(params, { replace: true });
  }

  // ANTES del return: un hook debajo de un return condicional cambia la
  // cantidad de hooks entre renders y React tira la pantalla entera.
  const recorrido = useRecorrido('email', { hayDatos: messages.length > 0, listo: true });

  const showListArea = sectionMode !== 'history';
  const checkedArr = Array.from(checkedIds);
  const checkedThreadArr = Array.from(checkedThreadIds);

  return (
    <div className="flex flex-col h-full animate-fade-blur-in" style={{ background: 'var(--evo-bg)' }}>
      <Recorrido
        pasos={recorrido.pasos}
        abierto={recorrido.abierto}
        onCerrar={recorrido.cerrar}
        onTerminar={recorrido.terminar}
      />
      {confirmDialog}

      {/* FilterRow — replaces tabs */}
      <FilterRow>
        <FilterRow.Search
          value={emailSearch}
          onChange={v => { setEmailSearch(v); setPage(1); }}
          onClear={() => { setEmailSearch(''); setDebouncedSearch(''); }}
          placeholder={t('common.search')}
        />
        {(sectionMode === 'main' || sectionMode === 'followup') && (
          // El div es solo el ancla del recorrido; `display: flex` no cambia el
          // dibujo y evita sumarle una prop al Dropdown, que se usa en todos
          // los módulos.
          <div data-tour-id="email-vistas" style={{ display: 'flex' }}>
          <FilterRow.Dropdown<string>
            value={sectionMode === 'followup' ? 'followup' : viewFilter}
            options={[
              { value: 'all', label: t('email.tabs.all') },
              { value: 'unread', label: t('email.tabs.unread') },
              {
                value: 'followup',
                label: (stats?.pendingFollowup ?? 0) > 0
                  ? `${t('email.tabs.followup')} (${stats!.pendingFollowup})`
                  : t('email.tabs.followup'),
              },
              { value: 'sent', label: t('email.tabs.sent') },
              { value: 'drafts', label: t('email.tabs.drafts') },
              { value: 'all_categories', label: t('email.tabs.categories') },
              { value: 'trash', label: t('email.tabs.trash') },
            ]}
            onChange={val => {
              if (val === 'followup') handleSectionChange('followup');
              else handleViewChange(val as ViewFilter);
            }}
            placeholder={t('email.tabs.all')}
          />
          </div>
        )}
        <div data-tour-id="email-categorias" style={{ display: 'flex' }}>
        <FilterRow.Dropdown<string>
          value={activeCategory ?? ''}
          placeholder={t('contacts.allCategories')}
          options={[
            { value: '', label: t('contacts.allCategories') },
            ...(uncategorizedCount > 0
              ? [{ value: '__unclassified__', label: `${t('email.uncategorizedChip')} (${uncategorizedCount})` }]
              : []),
            ...categoriesForFilter.map(c => ({
              value: c.category,
              label: `${t(`categories.${c.category}`, { defaultValue: c.category })} (${c.count})`,
            })),
          ]}
          onChange={val => handleCategoryChange(val || null)}
        />
        </div>
        <div data-tour-id="email-modo" style={{ flexShrink: 0, marginLeft: 'auto' }}>
          <SegmentedControl<'messages' | 'threads'>
            value={viewMode}
            onChange={(m) => { setViewMode(m); setSelectedId(null); setSelectedThreadId(null); setPage(1); setCheckedIds(new Set()); setCheckedThreadIds(new Set()); }}
            size="sm"
            options={[
              { value: 'messages', label: t('email.viewModeMessages') },
              { value: 'threads', label: t('email.viewModeThreads') },
            ]}
          />
        </div>
      </FilterRow>

      {/* Selection bar — full panel width */}
      <SelectionActionBar
        visible={viewMode === 'threads' ? checkedThreadIds.size > 0 : isDraftsView ? checkedDraftIds.size > 0 : checkedIds.size > 0}
        countLabel={t('contacts.selected', { count: viewMode === 'threads' ? checkedThreadIds.size : isDraftsView ? checkedDraftIds.size : checkedIds.size })}
        onClearSelection={() => { setCheckedIds(new Set()); setCheckedThreadIds(new Set()); setCheckedDraftIds(new Set()); }}
      >
        {viewMode === 'threads' ? (
          <>
            <SelectionActionBar.Action
              label={t('email.bulk.markRead')}
              loading={bulkThreadAction.isPending}
              onClick={() => bulkThreadAction.mutate({ threadIds: checkedThreadArr, action: 'mark_read' }, { onSuccess: () => setCheckedThreadIds(new Set()) })}
            />
            <SelectionActionBar.Action
              label={t('email.bulk.markUnread')}
              loading={bulkThreadAction.isPending}
              onClick={() => bulkThreadAction.mutate({ threadIds: checkedThreadArr, action: 'mark_unread' }, { onSuccess: () => setCheckedThreadIds(new Set()) })}
            />
            <SelectionActionBar.Divider />
            <Dropdown<string>
              value={null}
              placeholder={t('email.bulk.categorize')}
              options={categoryNames.map(c => ({ value: c, label: t('categories.' + c, { defaultValue: c }) }))}
              onChange={val => {
                if (val) bulkThreadAction.mutate({ threadIds: checkedThreadArr, action: 'set_category', value: val }, { onSuccess: () => setCheckedThreadIds(new Set()) });
              }}
              size="sm"
            />
            <SelectionActionBar.Divider />
            <SelectionActionBar.IconAction
              icon="trash"
              label={t('email.trash.moveToTrash')}
              tone="danger"
              loading={bulkThreadAction.isPending}
              onClick={() => bulkThreadAction.mutate({ threadIds: checkedThreadArr, action: 'trash' }, { onSuccess: () => setCheckedThreadIds(new Set()) })}
              confirmRequired
            />
          </>
        ) : isTrashView ? (
          <>
            <SelectionActionBar.Action
              label={t('email.trash.restore')}
              loading={bulkRestore.isPending}
              onClick={() => bulkRestore.mutate(checkedArr, { onSuccess: () => setCheckedIds(new Set()) })}
            />
            <SelectionActionBar.Action
              label={t('email.trash.deletePermanent')}
              tone="danger"
              loading={permanentDelete.isPending}
              onClick={() => permanentDelete.mutate(checkedArr, { onSuccess: () => setCheckedIds(new Set()) })}
              confirmRequired
            />
          </>
        ) : isDraftsView ? (
          <SelectionActionBar.Action
            label={t('email.drafts.discard')}
            tone="danger"
            loading={discardDraftMut.isPending}
            onClick={handleBulkDiscardDrafts}
            confirmRequired
          />
        ) : (
          <>
            <SelectionActionBar.Action
              label={t('email.bulk.markRead')}
              onClick={() => { checkedArr.forEach(id => markAsRead.mutate(id)); setCheckedIds(new Set()); }}
            />
            <SelectionActionBar.Action
              label={t('email.bulk.markUnread')}
              onClick={() => { checkedArr.forEach(id => markAsUnread.mutate(id)); setCheckedIds(new Set()); }}
            />
            <SelectionActionBar.Divider />
            <Dropdown<string>
              value={null}
              placeholder={t('email.bulk.categorize')}
              options={[
                ...categoryNames.map(c => ({ value: c, label: t('categories.' + c, { defaultValue: c }) })),
                { value: '__new__', label: t('email.bulk.newCategory') },
              ]}
              onChange={val => {
                if (val === '__new__') { setNewCatModal(true); return; }
                if (val) bulkCategorize.mutate({ ids: checkedArr, category: val }, { onSuccess: () => setCheckedIds(new Set()) });
              }}
              size="sm"
            />
            <SelectionActionBar.Divider />
            <SelectionActionBar.IconAction
              icon="bell"
              label={t('email.bulk.markFollowup')}
              tone="amber"
              loading={bulkFollowup.isPending}
              onClick={() => bulkFollowup.mutate({ ids: checkedArr, value: true }, { onSuccess: () => setCheckedIds(new Set()) })}
            />
            <SelectionActionBar.IconAction
              icon="bell-off"
              label={t('email.bulk.removeFollowup')}
              loading={bulkFollowup.isPending}
              onClick={() => bulkFollowup.mutate({ ids: checkedArr, value: false }, { onSuccess: () => setCheckedIds(new Set()) })}
            />
            <SelectionActionBar.Divider />
            <SelectionActionBar.IconAction
              icon="trash"
              label={t('email.trash.moveToTrash')}
              tone="danger"
              loading={bulkTrash.isPending}
              onClick={() => bulkTrash.mutate(checkedArr, { onSuccess: () => setCheckedIds(new Set()) })}
              confirmRequired
            />
          </>
        )}
      </SelectionActionBar>

      {/* Content */}
      {sectionMode === 'history' ? (
        <div className="flex-1 overflow-y-auto px-5 py-4"><RunsTable /></div>
      ) : (
        <div className="flex flex-1 overflow-hidden" data-tour-id="email-lista">
          {/* Email list */}
          <div className="flex flex-col shrink-0 overflow-hidden" style={{ width: 'clamp(300px, 33%, 480px)', borderRight: '1px solid var(--evo-stroke)' }}>
            {/* New category modal */}
            <Modal
              open={newCatModal}
              onClose={() => setNewCatModal(false)}
              size="sm"
              title={t('email.bulk.newCategoryTitle')}
              footer={
                <>
                  <Button
                    variant="ghost"
                    tone="muted"
                    size="sm"
                    onClick={() => setNewCatModal(false)}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    variant="solid"
                    tone="cyan"
                    size="sm"
                    loading={bulkCategorize.isPending}
                    disabled={!newCatInput.trim()}
                    onClick={() => {
                      if (!newCatInput.trim()) return;
                      bulkCategorize.mutate(
                        { ids: checkedArr, category: newCatInput.trim() },
                        { onSuccess: () => { setCheckedIds(new Set()); setNewCatModal(false); setNewCatInput(''); } }
                      );
                    }}
                  >
                    {t('email.bulk.newCategoryCreate')}
                  </Button>
                </>
              }
            >
              <Input
                value={newCatInput}
                onChange={setNewCatInput}
                onKeyDown={e => {
                  if (e.key === 'Enter' && newCatInput.trim()) {
                    bulkCategorize.mutate(
                      { ids: checkedArr, category: newCatInput.trim() },
                      { onSuccess: () => { setCheckedIds(new Set()); setNewCatModal(false); setNewCatInput(''); } }
                    );
                  }
                }}
                placeholder={t('email.bulk.newCategoryPlaceholder')}
                autoFocus
                fullWidth
              />
            </Modal>
            {/* Followup sub-filters */}
            {sectionMode === 'followup' && (
              <div className="flex gap-1 px-3 py-1.5 shrink-0 flex-wrap" style={{ borderBottom: '1px solid var(--evo-stroke)', background: 'var(--evo-bg-2)' }}>
                {([null, 'alta', 'media', 'baja', 'uncertain'] as const).map(sub => (
                  <Button key={sub ?? 'all'}
                    size="sm"
                    variant={followupSubFilter === sub ? 'solid' : 'ghost'}
                    tone={followupSubFilter === sub
                      ? (sub === null ? 'cyan' : sub === 'alta' ? 'danger' : sub === 'media' ? 'amber' : sub === 'baja' ? 'yellow' : 'muted')
                      : 'muted'}
                    onClick={() => { setFollowupSubFilter(sub); setPage(1); }}
                  >
                    {sub === null ? t('email.followup.tab_all') : sub === 'alta' ? t('email.followup.tab_high') : sub === 'media' ? t('email.followup.tab_medium') : sub === 'baja' ? t('email.followup.tab_low') : t('email.followup.tab_uncertain')}
                  </Button>
                ))}
              </div>
            )}

            {/* Select-all header */}
            {showListArea && !isDraftsView && viewMode !== 'threads' && messages.length > 0 && (
              <ListToolbar
                allSelected={checkedIds.size === messages.length && messages.length > 0}
                someSelected={checkedIds.size > 0 && checkedIds.size < messages.length}
                onSelectAll={toggleCheckAll}
                onExpandAll={expandAll}
                onCollapseAll={collapseAll}
                expandLabel={t('email.expandAll')}
                collapseLabel={t('email.collapseAll')}
              >
                {isTrashView && (
                  <Button variant="outline" tone="danger" size="sm"
                    loading={emptyTrash.isPending}
                    onClick={() => { if (confirm(t('email.trash.confirmEmpty'))) emptyTrash.mutate(); }}
                  >
                    {t('email.trash.emptyTrash')}
                  </Button>
                )}
              </ListToolbar>
            )}
            {viewMode === 'threads' && !isDraftsView && (threadsData?.threads ?? []).length > 0 && (
              <ListToolbar
                allSelected={checkedThreadIds.size === (threadsData?.threads?.length ?? 0) && (threadsData?.threads?.length ?? 0) > 0}
                someSelected={checkedThreadIds.size > 0 && checkedThreadIds.size < (threadsData?.threads?.length ?? 0)}
                onSelectAll={toggleCheckAllThreads}
              />
            )}
            <div className="flex-1 overflow-y-auto">
              {/* ── Drafts list ─────────────────────────────────────────── */}
              {isDraftsView && drafts.length === 0 && (
                <EmptyState size="md" reserveSpace title={t('email.empty.drafts')} />
              )}
              {isDraftsView && drafts.map(d => (
                <div key={d.id} style={{ display: 'flex', alignItems: 'flex-start', borderBottom: '1px solid var(--evo-stroke)' }}>
                  <div
                    style={{ display: 'flex', alignItems: 'flex-start', padding: '14px 8px 0 20px', flexShrink: 0, cursor: 'pointer' }}
                    onClick={e => { e.stopPropagation(); toggleCheckDraft(d.id); }}
                  >
                    <div style={{ marginTop: 2, pointerEvents: 'none' }}>
                      <Checkbox checked={checkedDraftIds.has(d.id)} onChange={() => {}} />
                    </div>
                  </div>
                  <div
                    onClick={() => openDraft(d)}
                    style={{ flex: 1, minWidth: 0, padding: '10px 12px 10px 0', cursor: 'pointer' }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium truncate" style={{ color: 'var(--evo-fg-2)', maxWidth: '65%' }}>
                        {d.toAddresses || '—'}
                      </span>
                      <span className="text-xs shrink-0" style={{ color: 'var(--evo-fg-3)' }}>{fmtDate(d.updatedAt)}</span>
                    </div>
                    <div className="text-xs mt-0.5 font-medium" style={{ color: 'var(--evo-fg-2)' }}>
                      {d.subject || t('email.drafts.noSubject')}
                    </div>
                    <div className="flex justify-end mt-1">
                      <Button
                        variant="outline"
                        tone="danger"
                        size="xs"
                        onClick={e => { e.stopPropagation(); void handleDiscardDraft(d.id); }}
                      >
                        {t('email.drafts.discard')}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}

              {/* ── Threads list ────────────────────────────────────────── */}
              {viewMode === 'threads' && !isDraftsView && threadsLoading && (
                <LoadingState size="sm" fullHeight />
              )}
              {viewMode === 'threads' && !isDraftsView && !threadsLoading && (threadsData?.threads ?? []).length === 0 && (
                <EmptyState size="md" reserveSpace title={t('email.threads.empty')} />
              )}
              {viewMode === 'threads' && !isDraftsView && threads.map((thread: ThreadSummary) => (
                <div key={thread.id} data-list-item-id={thread.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    borderBottom: '1px solid var(--evo-stroke)',
                    background: selectedThreadId === thread.id ? 'color-mix(in srgb, var(--evo-cyan) 12%, transparent)' : 'transparent',
                  }}>
                  <div
                    style={{ display: 'flex', alignItems: 'flex-start', padding: '14px 8px 0 12px', flexShrink: 0 }}
                    onClick={e => { e.stopPropagation(); toggleCheckThread(thread.id); }}>
                    <div style={{ marginTop: 2, pointerEvents: 'none' }}>
                      <Checkbox checked={checkedThreadIds.has(thread.id)} onChange={() => {}} />
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Pressable fullWidth onPress={() => setSelectedThreadId(thread.id)}>
                      <div className="text-left" style={{ padding: '10px 12px 10px 4px' }}>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold truncate flex-1"
                            style={{ color: thread.hasUnread ? 'var(--evo-fg-1)' : 'var(--evo-fg-2)' }}>
                            {thread.subjectDisplay || thread.subjectNormalized || t('email.drafts.noSubject')}
                          </span>
                          {thread.hasUnread ? <span className="shrink-0 w-2 h-2 rounded-full" style={{ background: 'var(--evo-cyan)' }} /> : null}
                          {thread.messageCount > 1 && (
                            <span className="shrink-0 text-xs px-1.5 py-0.5 rounded-full"
                              style={{ background: 'var(--evo-bg-2)', color: 'var(--evo-fg-3)', border: '1px solid var(--evo-stroke)', fontSize: 10 }}>
                              {t('email.threads.msgCount', { count: thread.messageCount })}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs truncate flex-1" style={{ color: 'var(--evo-fg-3)' }}>{thread.lastFrom ?? ''}</span>
                          <span className="text-xs shrink-0" style={{ color: 'var(--evo-fg-3)' }}>{fmtDate(thread.lastMessageAt)}</span>
                        </div>
                        {thread.lastMessagePreview && (
                          <div className="text-xs mt-0.5 truncate" style={{ color: 'var(--evo-fg-3)' }}>
                            {thread.lastMessagePreview.slice(0, 120)}
                          </div>
                        )}
                      </div>
                    </Pressable>
                  </div>
                </div>
              ))}

              {/* ── Email list ──────────────────────────────────────────── */}
              {viewMode !== 'threads' && !isDraftsView && isLoading && <LoadingState size="sm" fullHeight />}
              {viewMode !== 'threads' && !isDraftsView && !isLoading && messages.length === 0 && (
                <EmptyState
                  size="md"
                  reserveSpace
                  title={
                    sectionMode === 'followup'
                      ? t('email.empty.followup')
                      : isTrashView
                      ? t('email.empty.trash')
                      : isSentView
                      ? t('email.empty.sent')
                      : viewFilter === 'all_categories'
                      ? t('email.empty.uncategorized')
                      : t('email.empty.default')
                  }
                />
              )}
              {viewMode !== 'threads' && !isDraftsView && (
                <TimeBandedList
                  items={messages}
                  getTimestamp={msg => msg.timestamp}
                  getId={msg => msg.id}
                  isUnread={msg => msg.isRead === 0}
                  checkedIds={checkedIds}
                  onBandCheck={handleBandCheck}
                  collapsedBands={collapsedBands}
                  onToggleBand={toggleBand}
                  renderItem={msg => isTrashView ? (
                    // DS-TODO: migrar a ListItem cuando se unifique módulo Email
                    <div style={{ display: 'flex', alignItems: 'flex-start', borderBottom: '1px solid var(--evo-stroke)' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', padding: '14px 8px 0 20px', flexShrink: 0 }} onClick={e => { e.stopPropagation(); toggleCheck(msg.id); }}>
                        <div style={{ marginTop: 2, pointerEvents: 'none' }}>
                          <Checkbox checked={checkedIds.has(msg.id)} onChange={() => {}} />
                        </div>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div onClick={() => handleSelectEmail(msg.id, msg.isRead)} style={{ padding: '10px 12px 6px', cursor: 'pointer',
                          background: selectedId === msg.id ? 'var(--evo-cyan)18' : 'transparent' }}>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-medium truncate" style={{ color: 'var(--evo-fg-2)' }}>
                              {msg.fromEmail ?? '—'}
                            </span>
                            <span className="text-xs shrink-0" style={{ color: '#ef4444' }}>
                              {t('email.trash.deleteIn', { when: trashCountdown(msg.autoDeleteAt, t) })}
                            </span>
                          </div>
                          <div className="text-xs truncate mt-0.5" style={{ color: 'var(--evo-fg-3)' }}>{msg.subject || t('email.noSubject')}</div>
                          <div className="flex gap-2 mt-1">
                            <Button variant="outline" tone="cyan" size="sm" onClick={e => { e.stopPropagation(); bulkRestore.mutate([msg.id]); }}>{t('email.trash.restore')}</Button>
                            <Button variant="outline" tone="danger" size="sm" onClick={e => { e.stopPropagation(); if (confirm(t('email.trash.confirmSingleDelete'))) permanentDelete.mutate([msg.id]); }}>{t('email.trash.delete')}</Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      data-list-item-id={msg.id}
                      data-tour-id={msg.id === messages[0]?.id ? 'email-fila' : undefined}
                    >
                      <ListItem
                        active={selectedId === msg.id}
                        selected={checkedIds.has(msg.id)}
                        onSelect={() => toggleCheck(msg.id)}
                        onClick={() => handleSelectEmail(msg.id, msg.isRead)}
                        title={msg.fromEmail ?? '—'}
                        subtitle={`${msg.subject || t('email.noSubject')}${msg.body ? ` — ${stripHtml(msg.body).slice(0, 100)}` : ''}`}
                        subtitleLines={2}
                        meta={ago(msg.timestamp, t)}
                        badges={[
                          msg.needsFollowup ? (
                            <span
                              key="fu"
                              title={t('email.badge.followup')}
                              aria-label={t('email.badge.followup')}
                              style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                width: 'var(--evo-pill-h-xs)', height: 'var(--evo-pill-h-xs)', padding: 0,
                                color: 'var(--evo-amber)', background: 'transparent',
                                border: '1px solid var(--evo-amber)', borderRadius: 'var(--evo-r-pill)',
                                boxSizing: 'border-box', flexShrink: 0,
                              }}
                            >
                              <Icon name="bell" size={11} />
                            </span>
                          ) : null,
                          msg.classifiedAs ? <Pill key="cat" tone="cyan" size="xs">{t(`categories.${msg.classifiedAs}`, { defaultValue: msg.classifiedAs })}</Pill> : null,
                        ]}
                        unread={msg.isRead === 0}
                      />
                    </div>
                  )}
                  loadMoreSentinel={
                    <div ref={sentinelRef} className="py-3 text-center text-xs" style={{ color: 'var(--evo-fg-3)', minHeight: 4 }}>
                      {isFetchingNextPage ? t('common.loading') : ''}
                    </div>
                  }
                />
              )}
            </div>
          </div>

          {/* Detail / Composer panel (fills remaining space) */}
          <div className="flex flex-col overflow-hidden" style={{ flex: '1 1 0' }}>
            {composeState ? (
              <EmailComposer
                inline
                draft={composeState.draft}
                type={composeState.type}
                {...(composeState.draftId !== undefined ? { savedDraftId: composeState.draftId } : {})}
                onClose={() => { setComposeState(null); setComposerRecipient(''); }}
                onSent={() => { setComposeState(null); setComposerRecipient(''); }}
                onRecipientChange={setComposerRecipient}
              />
            ) : isDraftsView ? (
              <div className="flex-1 flex items-center justify-center text-xs" style={{ color: 'var(--evo-fg-3)' }}>
                {t('email.drafts.selectPrompt')}
              </div>
            ) : viewMode === 'threads' ? (
              selectedThreadId == null ? (
                <div className="flex-1 flex items-center justify-center text-xs" style={{ color: 'var(--evo-fg-3)' }}>
                  {t('email.selectEmailPrompt')}
                </div>
              ) : (
                <ThreadDetailView
                  detail={threadDetail}
                  onCompose={(draft, type) => {
                    setComposerRecipient(draft.to[0] ?? '');
                    setComposeState({ draft, type });
                  }}
                />
              )
            ) : selectedId == null ? (
              <div className="flex flex-col h-full">
                <div
                  className="shrink-0"
                  style={{
                    height: 'var(--evo-toolbar-height)',
                    padding: '0 var(--evo-space-5)',
                    borderBottom: '1px solid var(--evo-stroke)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--evo-space-2)',
                    opacity: 0.4,
                    pointerEvents: 'none',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }} />
                  <div style={{ display: 'flex', gap: 'var(--evo-space-2)', flexShrink: 0 }}>
                    {(['reply', 'reply-all', 'forward'] as const).map(key => (
                      <IconButton
                        key={key}
                        variant="outline"
                        tone="muted"
                        size="md"
                        disabled
                        icon={<Icon name={key} size={15} />}
                        ariaLabel={key === 'reply' ? t('email.reply') : key === 'reply-all' ? t('email.replyAll') : t('email.forward')}
                        tooltip={key === 'reply' ? t('email.reply') : key === 'reply-all' ? t('email.replyAll') : t('email.forward')}
                      />
                    ))}
                  </div>
                </div>
                <div
                  className="shrink-0"
                  style={{
                    height: 'var(--evo-toolbar-height)',
                    borderBottom: '1px solid var(--evo-stroke)',
                    opacity: 0.4,
                    pointerEvents: 'none',
                  }}
                />
                <div className="flex-1 flex items-center justify-center text-xs" style={{ color: 'var(--evo-fg-3)' }}>
                  {t('email.selectEmailPrompt')}
                </div>
              </div>
            ) : (
              <DetailPanel
                messageId={selectedId}
                categoryOptions={categoryNames}
                onCategoryChange={(id, cat, fromEmail) => updateCategory.mutate({ id, category: cat, fromEmail })}
                onSelectMessage={(id) => {
                  setSelectedId(id);
                  setSearchParams({ id: String(id) }, { replace: true });
                }}
                onClose={() => {
                  setSelectedId(null);
                  const params: Record<string, string> = {};
                  if (sectionMode !== 'main') params['section'] = sectionMode;
                  else if (viewFilter !== 'all') params['view'] = viewFilter;
                  setSearchParams(params, { replace: true });
                }}
                onCompose={(draft, type) => {
                  setComposerRecipient(draft.to[0] ?? '');
                  setComposeState({ draft, type });
                }}
              />
            )}
          </div>

          {/* Claude panel — always visible */}
          <ClaudePanel context={
            composeState
              ? { type: 'compose', recipient: composerRecipient, draft: { subject: composeState.draft.subject, body: composeState.draft.bodyHtml } }
              : selectedId != null
              ? { type: 'email', messageId: selectedId }
              : { type: 'idle' }
          } />
        </div>
      )}

    </div>
  );
}
